import { useState } from "react";
import { Box, Button, FormControl, IconButton, InputLabel, MenuItem, Paper, Select, Typography, CircularProgress, FormControlLabel, Switch } from "@mui/material";
import { Search as SearchIcon, EventAvailable, CalendarMonth, ViewList, WarningAmber } from "@mui/icons-material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { pt } from "date-fns/locale";
import { useRecords } from "../../api/useRecords";
import { useReservedSlotsCalendar } from "./useReservedSlotsCalendar";
import { ReservedSlotsGrid } from "./grid/ReservedSlotsGrid";
import { GridLegend } from "./grid/GridLegend";
import { ReservedSlotsList } from "./list/ReservedSlotsList";
import { MissedSessionsList } from "./list/MissedSessionsList";
import { Clinic, Technician, FindAvailableSlotsResult } from "./types";
import { findAvailableSlots } from "./findAvailableSlots";
import { AvailableSlotsDrawer } from "./AvailableSlotsDrawer";
import { MENU_WIDTH } from "../../components/menu/Menu";
import { APP_CONTENT_PADDING } from "../../components/root/AppContent";
import { convertMUIUnitsToPx } from "../../utils/convertMUIUnitsToPx";

type ViewMode = "grid" | "list" | "missed";

type AppliedFilters = {
    startDate: Date;
    clinicId: number | "";
    therapistId: string | "";
};

export const ReservedSlotsCalendar = () => {
    const [startDate, setStartDate] = useState<Date>(new Date());
    const [clinicId, setClinicId] = useState<number | "">("");
    const [therapistId, setTherapistId] = useState<string | "">("");
    const [showNfb, setShowNfb] = useState<boolean>(true);
    const [showOnlyMismatched, setShowOnlyMismatched] = useState<boolean>(false);
    const [viewMode, setViewMode] = useState<ViewMode>("grid");

    const [appliedFilters, setAppliedFilters] = useState<AppliedFilters | null>(null);
    const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
    const [availableSlotsResults, setAvailableSlotsResults] = useState<FindAvailableSlotsResult | null>(null);
    const [searchedServices, setSearchedServices] = useState<{ psy: boolean; nfb: boolean }>({ psy: false, nfb: false });

    const { data: clinicsData } = useRecords("clinics", "*");
    const clinics = (clinicsData?.items ?? []) as Array<Clinic>;

    const { data: techniciansData } = useRecords("technicians", "id,specializations,user_id.id,user_id.first_name,user_id.last_name", {
        extraOptions: {
            filter: { _or: [{ disabled: { _neq: true } }, { disabled: { _null: true } }] },
            sort: "user_id.first_name",
        },
    });
    const allTechnicians = (techniciansData?.items ?? []) as Array<Technician & { specializations?: Array<string> }>;
    const technicians = allTechnicians.filter((tech) => tech.specializations?.includes("psicologia"));
    const allTherapistIds = technicians.map((tech) => tech.user_id.id);

    const therapistIdForQuery = viewMode === "missed" 
        ? allTherapistIds.join(",") 
        : (appliedFilters?.therapistId ?? "");

    const {
        allSlots,
        getSlotsForDateAndTime,
        getBookingsForDateAndTime,
        getAvailabilityStatus,
        getNfbCapacity,
        isNfbBlocked,
        getOccupancy,
        isLoading,
        refetch,
    } = useReservedSlotsCalendar({
        startDate: appliedFilters?.startDate ?? new Date(),
        clinicId: appliedFilters?.clinicId ?? "",
        therapistId: therapistIdForQuery,
        enabled: appliedFilters !== null,
    });

    const handleDateChange = (newValue: Date | null) => {
        const newDate = newValue ?? new Date();
        setStartDate(newDate);
    };

    const handleSearch = () => {
        const newFilters = { startDate, clinicId, therapistId };
        const filtersChanged = 
            appliedFilters?.startDate.getTime() !== newFilters.startDate.getTime() ||
            appliedFilters?.clinicId !== newFilters.clinicId ||
            appliedFilters?.therapistId !== newFilters.therapistId;

        if (filtersChanged) {
            setAppliedFilters(newFilters);
        } else {
            refetch();
        }
        setAvailableSlotsResults(null);
    };

    const hasTherapistSelected = appliedFilters?.therapistId !== "";
    const nfbClinicSelected = (appliedFilters?.clinicId ?? "") !== "";
    const canFindNfb = showNfb && nfbClinicSelected;

    const handleFindAvailableSlots = () => {
        if (!appliedFilters) return;

        const includePsy = hasTherapistSelected;
        const includeNfb = canFindNfb;

        const results = findAvailableSlots({
            startDate: appliedFilters.startDate,
            includePsy,
            includeNfb,
            getOccupancy,
            getNfbCapacity,
            getAvailabilityStatus,
        });

        setSearchedServices({ psy: includePsy, nfb: includeNfb });
        setAvailableSlotsResults(results);
        setDrawerOpen(true);
    };

    return (
        <Box sx={{ maxWidth: `calc(100vw - ${MENU_WIDTH}px - ${convertMUIUnitsToPx(APP_CONTENT_PADDING) * 2}px)` }}>
            <Paper elevation={2} sx={{ p: 4, mb: 3 }}>
                <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", flexWrap: "wrap" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
                        <Typography variant="h5" color="primary" fontWeight="bold">
                            Horários Reservados
                        </Typography>
                        <IconButton
                            onClick={() => setViewMode("grid")}
                            color={viewMode === "grid" ? "primary" : "default"}
                            size="small"
                            title="Vista Calendário"
                        >
                            <CalendarMonth />
                        </IconButton>
                        <IconButton
                            onClick={() => setViewMode("list")}
                            color={viewMode === "list" ? "primary" : "default"}
                            size="small"
                            title="Vista Lista"
                        >
                            <ViewList />
                        </IconButton>
                        <IconButton
                            onClick={() => {
                                setViewMode("missed");
                                setTherapistId("");
                            }}
                            color={viewMode === "missed" ? "warning" : "default"}
                            size="small"
                            title="Sessões Falhadas"
                        >
                            <WarningAmber />
                        </IconButton>
                    </Box>
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={pt}>
                        <DatePicker
                            label="Data de Início"
                            value={startDate}
                            onChange={handleDateChange}
                            slotProps={{
                                textField: { size: "small", sx: { minWidth: 160 } },
                            }}
                        />
                    </LocalizationProvider>

                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>Clínica</InputLabel>
                        <Select value={clinicId} label="Clínica" onChange={(e) => setClinicId(e.target.value as number | "")}>
                            <MenuItem value="">Todas</MenuItem>
                            {clinics.map((clinic) => (
                                <MenuItem key={clinic.id} value={clinic.id}>
                                    {clinic.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 220 }} disabled={viewMode === "missed"}>
                        <InputLabel>Psicóloga</InputLabel>
                        <Select value={therapistId} label="Psicóloga" onChange={(e) => setTherapistId(e.target.value as string)}>
                            <MenuItem value="">Sem psicóloga</MenuItem>
                            {technicians.map((tech) => (
                                <MenuItem key={tech.id} value={tech.user_id.id}>
                                    {tech.user_id.first_name} {tech.user_id.last_name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                        onClick={handleSearch}
                        disabled={isLoading}
                        sx={{ mt: 0.5 }}
                    >
                        Procurar
                    </Button>

                    <Box sx={{ display: "flex", flexDirection: "column", ml: 2 }}>
                        <FormControlLabel
                            control={<Switch checked={showNfb} onChange={(e) => setShowNfb(e.target.checked)} size="small" />}
                            label="Mostrar Neurofeedback"
                        />
                        <FormControlLabel
                            control={<Switch checked={showOnlyMismatched} onChange={(e) => setShowOnlyMismatched(e.target.checked)} size="small" />}
                            label="Mostrar apenas com problemas"
                        />
                    </Box>
                    {appliedFilters && viewMode === "grid" && (hasTherapistSelected || (showNfb && nfbClinicSelected)) && (
                        <Box sx={{ ml: "auto", alignSelf: "center" }}>
                            <GridLegend showPsy={hasTherapistSelected} showNfb={showNfb && nfbClinicSelected} />
                        </Box>
                    )}
                </Box>
            </Paper>

            {appliedFilters && !isLoading && (
                <Paper elevation={2} sx={{ p: 2 }}>
                    {viewMode === "grid" && (hasTherapistSelected || canFindNfb) && (
                        <Box sx={{ mb: 2 }}>
                            <Button
                                variant="outlined"
                                color="secondary"
                                startIcon={<EventAvailable />}
                                onClick={handleFindAvailableSlots}
                            >
                                Encontrar Horários Possíveis
                            </Button>
                        </Box>
                    )}
                    {viewMode === "grid" && showNfb && !nfbClinicSelected && (
                        <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
                            Selecione uma clínica para ver os horários de Neurofeedback.
                        </Typography>
                    )}
                    {viewMode === "grid" && (
                        <ReservedSlotsGrid
                            startDate={appliedFilters.startDate}
                            getSlotsForDateAndTime={getSlotsForDateAndTime}
                            getBookingsForDateAndTime={getBookingsForDateAndTime}
                            getAvailabilityStatus={getAvailabilityStatus}
                            getNfbCapacity={getNfbCapacity}
                            isNfbBlocked={isNfbBlocked}
                            showPsy={hasTherapistSelected}
                            showNfb={showNfb && nfbClinicSelected}
                            showOnlyMismatched={showOnlyMismatched}
                        />
                    )}
                    {viewMode === "list" && (
                        <ReservedSlotsList
                            startDate={appliedFilters.startDate}
                            allSlots={allSlots}
                            showNfb={showNfb}
                            hasTherapistFilter={hasTherapistSelected}
                            showOnlyMismatched={showOnlyMismatched}
                        />
                    )}
                    {viewMode === "missed" && (
                        <MissedSessionsList allSlots={allSlots} showNfb={showNfb} />
                    )}
                </Paper>
            )}

            {!appliedFilters && (
                <Paper elevation={1} sx={{ p: 4, textAlign: "center" }}>
                    <Typography color="text.secondary">
                        Selecione os filtros e clique em "Procurar" para ver os horários reservados.
                    </Typography>
                </Paper>
            )}

            <AvailableSlotsDrawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                results={availableSlotsResults}
                includePsy={searchedServices.psy}
                includeNfb={searchedServices.nfb}
            />
        </Box>
    );
};
